import { createContext, useContext } from 'react';
import { problemMessage, type Schemas } from '../../api/client';
import { uploadFile } from '../../api/files';
import { getToken } from '../../auth/tokenSource';

/**
 * Soubory vložené do textu (popis zakázky, poznámky). Jsou to obyčejné přílohy zakázky —
 * text si drží jen jejich id. Stahují se s tokenem v hlavičce (do adresy ho nedáváme),
 * proto se obrázek nedá vložit jako <img src>, ale načte se jako blob.
 */
export type Attachment = Schemas['AttachmentResponse'];

/** Zakázka, ke které text patří. Bez ní (nová zakázka) se soubory vkládat nedají. */
export const MediaContext = createContext<number | null>(null);
export const useMediaContract = () => useContext(MediaContext);

/** Obrázky, které prohlížeč umí zobrazit. HEIC z iPhonu ne — ten se vloží jako soubor. */
const displayableImage = /^image\/(jpeg|png|gif|webp|avif)$/;
export const isDisplayableImage = (contentType: string) => displayableImage.test(contentType);
export const isPdf = (contentType: string) => contentType === 'application/pdf';

const cache = new Map<string, Promise<string>>();

/**
 * Blob URL přílohy. Server posílá vše jako octet-stream (ochrana proti XSS) — skutečný typ
 * doplníme sami, jinak by PDF v náhledu nešlo otevřít. Jedno stažení na stránku.
 */
export function attachmentUrl(contractId: number, id: number, contentType: string): Promise<string> {
  const key = `${contractId}:${id}`;
  let pending = cache.get(key);

  if (!pending) {
    pending = (async () => {
      const token = await getToken();
      const response = await fetch(`/api/v1/contracts/${contractId}/attachments/${id}/content`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      if (!response.ok) throw new Error('Soubor se nepodařilo načíst.');
      const data = await response.arrayBuffer();
      return URL.createObjectURL(new Blob([data], { type: contentType }));
    })();
    // Nepovedene nacteni se nepamatuje - pristi zobrazeni to zkusi znovu.
    pending.catch(() => cache.delete(key));
    cache.set(key, pending);
  }

  return pending;
}

/** Nahraje soubor jako přílohu zakázky. Stejný soubor znovu nenahraje — vrátí ten existující. */
export async function uploadForText(contractId: number, file: File): Promise<Attachment> {
  const category = file.type.startsWith('image/') ? 'Photo' : 'Input';
  const response = await uploadFile(`/api/v1/contracts/${contractId}/attachments?reuseExisting=true`, file, category);
  const body = await response.json().catch(() => null);

  if (!response.ok) throw new Error(`${file.name}: ${problemMessage(body)}`);
  return body as Attachment;
}
